import React, { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Flame, Clock, TrendingDown, Reply, Brain, Search, Phone, Send, Activity, PieChart, Users, Megaphone, Target,
} from 'lucide-react';
import { fmtCr, getActionLabel, isVisibleAction } from './billingHelpers';
import { billingAPI } from '../../lib/api';

const CAT_CONFIG = {
  ai:          { label: 'AI Brain',          ring: '#8b5cf6', bg: 'bg-violet-500', text: 'text-violet-400', soft: 'bg-violet-500/10', icon: Brain },
  prospecting: { label: 'Prospecting & Data', ring: '#06b6d4', bg: 'bg-cyan-500',   text: 'text-cyan-400',   soft: 'bg-cyan-500/10',   icon: Search },
  outreach:    { label: 'Outreach',          ring: '#38bdf8', bg: 'bg-sky-500',    text: 'text-sky-400',    soft: 'bg-sky-500/10',    icon: Send },
  voice:       { label: 'Voice Calls',       ring: '#f59e0b', bg: 'bg-amber-500',  text: 'text-amber-400',  soft: 'bg-amber-500/10',  icon: Phone },
  other:       { label: 'Other',             ring: '#64748b', bg: 'bg-slate-500',  text: 'text-slate-400',  soft: 'bg-slate-500/10',  icon: Activity },
};
const ORDER = ['ai', 'prospecting', 'outreach', 'voice', 'other'];

function Donut({ segments, size = 148, strokeWidth = 18 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;
  const total = segments.reduce((a, s) => a + s.value, 0);
  let accumulated = 0;
  const arcs = total > 0 ? segments.map((seg) => {
    const pct = seg.value / total;
    const offset = circumference * (1 - pct);
    const rotation = (accumulated / total) * 360 - 90;
    accumulated += seg.value;
    return { ...seg, offset, rotation };
  }) : [];
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} data-testid="spend-donut-chart">
        <circle cx={center} cy={center} r={radius} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={strokeWidth} />
        {arcs.map((arc, i) => (
          <circle key={i} cx={center} cy={center} r={radius}
            fill="none" stroke={arc.color} strokeWidth={strokeWidth}
            strokeDasharray={circumference} strokeDashoffset={arc.offset} strokeLinecap="round"
            transform={`rotate(${arc.rotation} ${center} ${center})`}
            className="transition-all duration-700" style={{ opacity: 0.95 }} />
        ))}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-foreground tabular-nums">{Math.round(total).toLocaleString()}</span>
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Cr this cycle</span>
      </div>
    </div>
  );
}

function MiniBars({ data, height = 72 }) {
  if (!data || data.length === 0) return null;
  const maxVal = Math.max(...data.map(d => d.value), 0.001);
  return (
    <div className="flex items-end gap-[3px]" style={{ height }} data-testid="daily-trend-chart">
      {data.map((d, i) => {
        const h = Math.max((d.value / maxVal) * 100, 3);
        const show = i === 0 || i === data.length - 1 || i === Math.floor(data.length / 2);
        return (
          <div key={i} className="flex-1 flex flex-col items-center justify-end h-full gap-1" title={`${d.label}: ${fmtCr(d.value)} Cr`}>
            <div className="w-full rounded-t bg-indigo-500/70 hover:bg-indigo-400 transition-colors" style={{ height: `${h}%`, minHeight: 2 }} />
            {show && <span className="text-[9px] text-muted-foreground whitespace-nowrap">{d.label}</span>}
          </div>
        );
      })}
    </div>
  );
}

export function ConsolidatedSpendAnalytics({ costDashboard, usageHistory, creditSummary, cycleLabel }) {
  const cats = costDashboard?.categories_credits || {};
  const details = costDashboard?.category_details || {};
  const labels = costDashboard?.category_labels || {};

  const groups = useMemo(() => ORDER
    .map((key) => ({
      key,
      cfg: CAT_CONFIG[key],
      total: Math.round(Number(cats[key] || 0)),
      items: (details[key] || []).filter((it) => isVisibleAction(it.action) && (it.count > 0 || it.cost_credits > 0))
        .sort((a, b) => (b.cost_credits || 0) - (a.cost_credits || 0)),
    }))
    .filter((g) => g.total > 0 || g.items.length > 0), [cats, details]);

  const [activeCat, setActiveCat] = useState(null);
  const current = groups.find((g) => g.key === activeCat) || groups[0];

  const donutSegments = groups.map((g) => ({ value: g.total, color: g.cfg.ring, label: g.cfg.label }));
  const grandTotal = Math.round(Number(costDashboard?.month_total_spent_credits || 0));

  const dailyData = useMemo(() => {
    if (!usageHistory?.days) return [];
    return [...usageHistory.days].reverse().slice(-7).map((day) => {
      const d = new Date(day.date + 'T00:00:00');
      return { value: Number(day.credits_used || 0), label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) };
    });
  }, [usageHistory]);

  const dailyRate = Math.round(Number(costDashboard?.daily_rate_credits || 0));
  const estMonthly = Math.round(Number(costDashboard?.estimated_monthly_credits || 0));
  const outcomes = costDashboard?.outcomes || {};
  const cprCr = outcomes.cost_per_reply_credits;

  // Per-lead / per-campaign attribution (where credits went → results).
  // Refetches when the active category tab changes so the panel answers
  // "click a category → see which lead/campaign the credits went to".
  const [attr, setAttr] = useState(null);
  const [attrView, setAttrView] = useState('leads');
  const attrCat = (current?.key && ['ai', 'prospecting', 'outreach', 'voice'].includes(current.key)) ? current.key : 'all';
  useEffect(() => {
    billingAPI.getSpendAttribution('month', attrCat).then(r => setAttr(r.data)).catch(() => {});
  }, [attrCat]);

  if (!costDashboard || groups.length === 0) {
    return (
      <Card data-testid="consolidated-spend-analytics">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><PieChart className="h-4 w-4 text-indigo-400" />Where your credits go</CardTitle></CardHeader>
        <CardContent>
          <div className="text-center py-10 text-muted-foreground" data-testid="spend-analytics-empty">
            <Activity className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No spend yet this cycle.</p>
            <p className="text-xs">As your AI calls, messages and enriches leads, you&apos;ll see exactly what each action cost here.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="consolidated-spend-analytics" className="border-primary/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2"><PieChart className="h-4 w-4 text-indigo-400" />Where your credits go</CardTitle>
          <Badge variant="outline" className="text-[10px] font-medium text-muted-foreground" data-testid="spend-scope-badge">
            Scope: This billing cycle{cycleLabel ? ` · ${cycleLabel}` : ''}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {/* Stat chips */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <div className="rounded-lg border border-border bg-muted/20 p-3" data-testid="stat-cycle-spend">
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Flame className="h-3 w-3 text-orange-500" />Spent this cycle</div>
            <div className="text-xl font-bold tabular-nums mt-0.5">{grandTotal.toLocaleString()} <span className="text-xs font-normal text-muted-foreground">Cr</span></div>
          </div>
          <div className="rounded-lg border border-border bg-muted/20 p-3" data-testid="stat-daily-burn">
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><TrendingDown className="h-3 w-3 text-cyan-500" />Daily burn</div>
            <div className="text-xl font-bold tabular-nums mt-0.5">{dailyRate.toLocaleString()} <span className="text-xs font-normal text-muted-foreground">Cr/day</span></div>
          </div>
          <div className="rounded-lg border border-border bg-muted/20 p-3" data-testid="stat-est-monthly">
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Clock className="h-3 w-3 text-amber-500" />Est. this month</div>
            <div className="text-xl font-bold tabular-nums mt-0.5">{estMonthly.toLocaleString()} <span className="text-xs font-normal text-muted-foreground">Cr</span></div>
          </div>
          <div className="rounded-lg border border-border bg-muted/20 p-3" data-testid="stat-cost-per-reply">
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Reply className="h-3 w-3 text-emerald-500" />Cost / reply</div>
            <div className="text-xl font-bold tabular-nums mt-0.5">{cprCr != null ? `${Number(cprCr).toLocaleString()}` : '—'} <span className="text-xs font-normal text-muted-foreground">{cprCr != null ? 'Cr' : ''}</span></div>
            <div className="text-[10px] text-muted-foreground">{outcomes.replies || 0} repl{(outcomes.replies === 1) ? 'y' : 'ies'} this cycle</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: donut + legend + trend */}
          <div className="lg:col-span-5 lg:border-r lg:border-border/40 lg:pr-6">
            <div className="flex items-center gap-5">
              <Donut segments={donutSegments} />
              <div className="flex-1 space-y-2 min-w-0">
                {groups.map((g) => {
                  const pct = grandTotal > 0 ? Math.round((g.total / grandTotal) * 100) : 0;
                  return (
                    <button key={g.key} onClick={() => setActiveCat(g.key)}
                      className={`w-full flex items-center gap-2 rounded-md px-2 py-1 text-left transition-colors ${current?.key === g.key ? 'bg-muted/50' : 'hover:bg-muted/30'}`}
                      data-testid={`spend-legend-${g.key}`}>
                      <span className={`w-2.5 h-2.5 rounded-full ${g.cfg.bg}`} />
                      <span className="text-xs text-muted-foreground flex-1 truncate">{labels[g.key] || g.cfg.label}</span>
                      <span className="text-xs font-semibold tabular-nums">{pct}%</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {dailyData.length > 0 && (
              <div className="mt-5 pt-4 border-t border-border/40">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Daily spend</span>
                  <span className="text-[10px] text-muted-foreground">Last {dailyData.length} days</span>
                </div>
                <MiniBars data={dailyData} />
              </div>
            )}
          </div>

          {/* Right: category tabs + itemized consumers */}
          <div className="lg:col-span-7">
            <div className="flex flex-wrap gap-1.5 mb-4" data-testid="spend-category-tabs">
              {groups.map((g) => {
                const Icon = g.cfg.icon;
                const active = current?.key === g.key;
                return (
                  <button key={g.key} onClick={() => setActiveCat(g.key)}
                    className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-all ${active ? `${g.cfg.soft} ${g.cfg.text} border-current` : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'}`}
                    data-testid={`spend-tab-${g.key}`}>
                    <Icon className="h-3.5 w-3.5" />{labels[g.key] || g.cfg.label}
                  </button>
                );
              })}
            </div>
            {current && (
              <div data-testid={`spend-detail-${current.key}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold">{labels[current.key] || current.cfg.label}</span>
                  <Badge variant="outline" className="text-xs font-mono">{current.total.toLocaleString()} Cr</Badge>
                </div>
                <div className="space-y-3">
                  {current.items.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-4 text-center">No itemized activity in this category yet.</p>
                  ) : current.items.map((it) => {
                    const pct = current.total > 0 ? ((it.cost_credits || 0) / current.total) * 100 : 0;
                    return (
                      <div key={it.action}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium truncate">
                            {it.label || getActionLabel(it.action)}
                            {it.count > 0 && <span className="text-[10px] ml-1.5 text-muted-foreground">× {it.count.toLocaleString()}</span>}
                          </span>
                          <span className="text-xs font-semibold tabular-nums">{Number(it.cost_credits || 0).toLocaleString()} Cr</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className={`h-full rounded-full ${current.cfg.bg} transition-all duration-500`} style={{ width: `${Math.max(pct, 2)}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex items-center justify-between pt-4 mt-4 border-t border-border">
              <span className="text-sm font-semibold">Total this cycle</span>
              <span className="font-mono font-bold text-primary" data-testid="spend-grand-total">{grandTotal.toLocaleString()} Cr</span>
            </div>
          </div>
        </div>

        {/* Attribution — where credits went → results (by lead / campaign) */}
        {attr && ((attr.leads && attr.leads.length > 0) || (attr.campaigns && attr.campaigns.length > 0)) && (
          <div className="mt-6 pt-5 border-t border-border/40" data-testid="spend-attribution">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <span className="text-sm font-semibold flex items-center gap-2"><Target className="h-4 w-4 text-indigo-400" />Spend tied to results{attrCat !== 'all' ? <Badge variant="outline" className="text-[10px] font-normal">{labels[attrCat] || attrCat}</Badge> : null}</span>
              <div className="inline-flex rounded-lg border border-border p-0.5">
                <button onClick={() => setAttrView('leads')} className={`flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium transition-colors ${attrView === 'leads' ? 'bg-muted text-foreground' : 'text-muted-foreground'}`} data-testid="attr-tab-leads"><Users className="h-3.5 w-3.5" />By lead</button>
                <button onClick={() => setAttrView('campaigns')} className={`flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium transition-colors ${attrView === 'campaigns' ? 'bg-muted text-foreground' : 'text-muted-foreground'}`} data-testid="attr-tab-campaigns"><Megaphone className="h-3.5 w-3.5" />By campaign</button>
              </div>
            </div>
            {(() => {
              const rows = attrView === 'leads' ? attr.leads : attr.campaigns;
              if (!rows || rows.length === 0) {
                return <p className="text-xs text-muted-foreground py-3 text-center">{attrView === 'campaigns' ? 'No campaign-tagged spend yet — new campaign activity will appear here.' : 'No lead-tagged spend in this period.'}</p>;
              }
              const max = Math.max(...rows.map(r => r.cost_credits), 1);
              return (
                <div className="space-y-2.5">
                  {rows.map((r) => (
                    <div key={r.id} className="flex items-center gap-3" data-testid={`attr-row-${attrView}`}>
                      <span className="text-xs font-medium truncate w-40 shrink-0" title={r.name}>{r.name}</span>
                      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full bg-indigo-500/70" style={{ width: `${Math.max((r.cost_credits / max) * 100, 2)}%` }} />
                      </div>
                      <span className="text-xs font-semibold tabular-nums w-20 text-right">{r.cost_credits.toLocaleString()} Cr</span>
                      <span className="text-[10px] text-muted-foreground w-14 text-right">{r.count}×</span>
                    </div>
                  ))}
                  {attr.unattributed_credits > 0 && (
                    <div className="flex items-center gap-3 pt-1 opacity-60" data-testid="attr-unattributed">
                      <span className="text-xs italic w-40 shrink-0">Unattributed</span>
                      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-slate-500/50" style={{ width: '100%' }} /></div>
                      <span className="text-xs font-semibold tabular-nums w-20 text-right">{attr.unattributed_credits.toLocaleString()} Cr</span>
                      <span className="w-14" />
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ConsolidatedSpendAnalytics;
